import { c as _c } from "react/compiler-runtime";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useParams } from "next/navigation";
import { useState, useEffect } from "react";
import { useDeleteSetting } from "@/queries/theme/use-delete-setting";
import { ClubSetting } from "@/queries/theme/use-get-settings";
dayjs.extend(utc);
dayjs.extend(timezone);
const formatTime = (timeString: string) => {
  const timeMatch = timeString.match(/T(\d{2}:\d{2}):\d{2}/);
  return timeMatch ? timeMatch[1] : timeString;
};
const DAYS_OF_WEEK_MAP = {
  1: "Lundi",
  2: "Mardi",
  3: "Mercredi",
  4: "Jeudi",
  5: "Vendredi",
  6: "Samedi",
  7: "Dimanche"
} as const;
type DayWeekKey = keyof typeof DAYS_OF_WEEK_MAP;
interface SettingsListProps {
  settingsData?: {
    "hydra:member": ClubSetting[];
  };
  isLoadingSettings: boolean;
  isError: boolean;
}
const LoadingSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="space-y-4">{[1, 2, 3].map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const SettingsList = t0 => {
  const $ = _c(28);
  const {
    settingsData,
    isLoadingSettings,
    isError
  } = t0;
  const {
    id: clubSlug
  } = useParams();
  const {
    mutate: deleteSetting,
    isPending: isDeleting,
    isSuccess,
    isError: isErrorDelete
  } = useDeleteSetting();
  const [deletingSettingId, setDeletingSettingId] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  let t1;
  let t2;
  if ($[0] !== isSuccess) {
    t1 = () => {
      if (isSuccess) {
        setShowSuccessMessage(true);
        const timer = setTimeout(() => {
          setShowSuccessMessage(false);
        }, 3000);
        return () => clearTimeout(timer);
      }
    };
    t2 = [isSuccess];
    $[0] = isSuccess;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== clubSlug || $[4] !== deleteSetting) {
    t3 = settingId => {
      setDeletingSettingId(settingId);
      deleteSetting({
        settingId,
        clubSlug
      }, {
        onSettled: () => {
          setDeletingSettingId(null);
        }
      });
    };
    $[3] = clubSlug;
    $[4] = deleteSetting;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleDeleteSetting = t3;
  if (isLoadingSettings) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="mt-8"><h3 className="text-lg font-semibold mb-4">Paramètres actuels</h3><LoadingSkeleton /></div>;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h3 className="text-lg font-semibold">Paramètres actuels</h3>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== showSuccessMessage) {
    t5 = showSuccessMessage && <h3 className="text-lg font-semibold text-success2">Paramètre supprimé avec succès</h3>;
    $[8] = showSuccessMessage;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isErrorDelete) {
    t6 = isErrorDelete && <h3 className="text-lg font-semibold text-error2">Erreur lors de la suppression du paramètre</h3>;
    $[10] = isErrorDelete;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="flex justify-between mb-4">{t4}{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== isError) {
    t8 = isError && <h3 className="text-lg font-semibold text-error2">Erreur lors de la récupération des paramètres</h3>;
    $[15] = isError;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== deletingSettingId || $[18] !== handleDeleteSetting || $[19] !== isDeleting || $[20] !== settingsData) {
    t9 = settingsData?.["hydra:member"]?.map(setting => <div key={setting.id} className="p-4 bg-white rounded-lg border border-[#9b9b9b]"><div className="flex justify-between items-center"><div><p className="font-medium">Le {DAYS_OF_WEEK_MAP[setting.dayWeek as DayWeekKey]} de{" "}{formatTime(setting.openingTime)} à{" "}{formatTime(setting.closingTime)}</p>{setting.deletable ? <p className="text-sm text-gray-500">À partir du {dayjs(setting.startDate).format("DD/MM/YYYY")}</p> : <p className="text-sm text-gray-500">Paramètre actuel</p>}</div>{setting.deletable && <IconButton size="small" color="error" onClick={() => handleDeleteSetting(setting.id)} disabled={isDeleting}>{deletingSettingId === setting.id ? <CircularProgress size={20} color="error" /> : <DeleteIcon />}</IconButton>}</div></div>);
    $[17] = deletingSettingId;
    $[18] = handleDeleteSetting;
    $[19] = isDeleting;
    $[20] = settingsData;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <div className="space-y-4">{t8}{t9}</div>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t7) {
    t11 = <div className="mt-8">{t7}{t10}</div>;
    $[25] = t10;
    $[26] = t7;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
function _temp(index) {
  return <div key={index} className="p-4 bg-white rounded-lg border border-[#9b9b9b]"><div className="flex justify-between items-center"><div className="w-full"><Skeleton variant="text" width="60%" height={24} /><Skeleton variant="text" width="40%" height={20} /></div><Skeleton variant="circular" width={32} height={32} /></div></div>;
}